import React from "react";
import {
  ShoppingCart,
  Heart,
  Share2,
  Copy,
  Check,
  X,
  Truck,
  Mail,
} from "lucide-react";
import { WhatsappIcon } from "react-share";
import type { ProductInfoProps } from "./types";
// import SocialProofIndicators from "./SocialProofIndicators";

const ProductInfo: React.FC<ProductInfoProps> = ({
  product,
  hasDiscount,
  originalPrice,
  discountedPrice,
  quantity,
  onQuantityChange,
  onAddToCart,
  onWishlistClick,
  isWishlisted,
  wishlistLoading,
  user,
}) => {
  const [showShareMenu, setShowShareMenu] = React.useState(false);
  const [copied, setCopied] = React.useState(false);

  const currentUrl = window.location.href;
  const shareText = `Check out ${product.name} - ${currentUrl}`;

  const shareToWhatsApp = () => {
    const url = `https://wa.me/?text=${encodeURIComponent(shareText)}`;
    window.open(url, "_blank");
    setShowShareMenu(false);
  };

  const shareToFacebook = () => {
    const url = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(
      currentUrl,
    )}`;
    window.open(url, "_blank");
    setShowShareMenu(false);
  };

  const shareToTwitter = () => {
    const url = `https://twitter.com/intent/tweet?text=${encodeURIComponent(
      `Check out ${product.name}!`,
    )}&url=${encodeURIComponent(currentUrl)}`;
    window.open(url, "_blank");
    setShowShareMenu(false);
  };

  const shareToPinterest = () => {
    const imageUrl =
      product.image_paths && product.image_paths[0]
        ? product.image_paths[0]
        : "";
    const url = `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(
      currentUrl,
    )}&media=${encodeURIComponent(imageUrl)}&description=${encodeURIComponent(
      product.name || "",
    )}`;
    window.open(url, "_blank");
    setShowShareMenu(false);
  };

  const shareViaEmail = () => {
    const subject = `Check out ${product.name}`;
    const body = `I found this product and thought you might like it:\n\n${product.name}\n${currentUrl}`;
    window.location.href = `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
    setShowShareMenu(false);
  };

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(currentUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Fallback for older browsers
      const textArea = document.createElement("textarea");
      textArea.value = currentUrl;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const orderByWhatsapp = () => {
    const phone = "96181441597";
    const url = `https://wa.me/${phone}?text=${encodeURIComponent(shareText)}`;
    window.open(url, "_blank");
  };

  // Close menu when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest(".share-menu-container")) {
        setShowShareMenu(false);
      }
    };

    if (showShareMenu) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showShareMenu]);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-white mb-2">{product.name}</h1>

        {product.description && (
          <p className="text-neutral-400 leading-relaxed mb-4">
            {product.description}
          </p>
        )}

        {/* Social Proof Indicators */}
        {/* <SocialProofIndicators productId={product.id} /> */}
      </div>

      {/* Pricing */}
      <div className="border-t border-neutral-800 pt-6">
        <div className="flex items-baseline gap-3 mb-2">
          {hasDiscount ? (
            <>
              <span className="text-4xl font-bold text-white">
                ${discountedPrice.toFixed(2)}
              </span>
              <span className="text-xl text-neutral-500 line-through">
                ${originalPrice.toFixed(2)}
              </span>
              <span className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                {product.sale_rate}% OFF
              </span>
            </>
          ) : (
            <span className="text-4xl font-bold text-white">
              ${originalPrice.toFixed(2)}
            </span>
          )}
        </div>

        {hasDiscount && (
          <p className="text-sm text-green-400 font-medium mb-4">
            You save ${(originalPrice - discountedPrice).toFixed(2)}
          </p>
        )}

        {/* Delivery Estimate */}
        <div className="mt-4 p-3 bg-blue-500/10 border border-blue-500/20 rounded-lg">
          <div className="flex items-start gap-2">
            <Truck className="shrink-0 w-5 h-5 text-blue-400 mt-0.5" />
            <div>
              <p className="text-sm font-semibold text-blue-300">
                Estimated Delivery
              </p>
              <p className="text-sm text-blue-400/80">
                {new Date(
                  Date.now() + 3 * 24 * 60 * 60 * 1000,
                ).toLocaleDateString("en-US", {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                })}{" "}
                -{" "}
                {new Date(
                  Date.now() + 7 * 24 * 60 * 60 * 1000,
                ).toLocaleDateString("en-US", {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                })}
              </p>
            </div>
          </div>
        </div>

        {/* Stock Status */}
        <div className="flex items-center gap-2 mt-4">
          {(() => {
            const qty =
              typeof product.quantity === "number" ? product.quantity : 0;
            if (qty === 0) {
              return (
                <span
                  className="font-medium text-red-500"
                  role="status"
                  aria-live="polite"
                >
                  Out of stock
                </span>
              );
            }
            if (qty < 5) {
              return (
                <span
                  className="font-medium text-amber-500"
                  role="status"
                  aria-live="polite"
                >
                  Low in stock
                </span>
              );
            }
            return <span className="font-medium text-green-500">In Stock</span>;
          })()}
        </div>
      </div>

      {/* Quantity and Actions */}
      <div className="border-t border-neutral-800 pt-6">
        <div className="flex items-center gap-4 mb-6">
          <span className="text-sm font-medium text-neutral-300">
            Quantity:
          </span>
          <div className="flex items-center border border-neutral-700 bg-neutral-800 rounded-lg overflow-hidden">
            <button
              onClick={() => onQuantityChange("decrement")}
              className="p-3 hover:bg-neutral-700 transition-colors text-white"
              disabled={quantity <= 1 || (product.quantity || 0) === 0}
            >
              <span className="text-lg font-semibold">−</span>
            </button>
            <input
              type="number"
              value={
                quantity > (product.quantity || 0)
                  ? product.quantity || 0
                  : quantity
              }
              readOnly
              className="w-16 text-center border-0 bg-transparent text-white focus:ring-0 p-0"
            />
            <button
              onClick={() => onQuantityChange("increment")}
              className="p-3 hover:bg-neutral-700 transition-colors text-white"
              disabled={
                quantity >= (product.quantity || 0) ||
                (product.quantity || 0) === 0
              }
            >
              <span className="text-lg font-semibold">+</span>
            </button>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="grid grid-cols-[1fr_auto_auto] items-stretch gap-2 sm:flex sm:flex-row sm:gap-4">
          <button
            className="flex-1 bg-primary text-black py-4 px-6 rounded-xl hover:bg-primary/90 transition-all font-bold text-lg flex items-center justify-center gap-2 shadow-lg shadow-primary/20 col-span-1"
            disabled={(product.quantity || 0) === 0}
            onClick={onAddToCart}
          >
            <ShoppingCart className="w-5 h-5" />
            Add to Cart
          </button>

          <div className="flex gap-2 sm:ml-0 justify-self-end">
            <button
              className={`h-14 w-14 flex items-center justify-center border rounded-xl transition-all duration-200 ${
                isWishlisted
                  ? "bg-red-500/10 border-red-500/50 hover:bg-red-500/20"
                  : "bg-neutral-800 border-neutral-700 hover:border-neutral-500 hover:bg-neutral-700"
              }`}
              onClick={onWishlistClick}
              disabled={wishlistLoading}
              title={
                !user
                  ? "Login to use wishlist"
                  : isWishlisted
                    ? "Remove from wishlist"
                    : "Add to wishlist"
              }
            >
              <Heart
                className={`w-6 h-6 transition-colors ${
                  isWishlisted ? "fill-red-500 text-red-500" : "text-white"
                }`}
              />
            </button>
            <div className="relative share-menu-container">
              <button
                className="h-14 w-14 flex items-center justify-center border border-neutral-700 bg-neutral-800 rounded-xl hover:bg-neutral-700 transition-all duration-200 hover:border-neutral-500 active:scale-95"
                onClick={() => setShowShareMenu((prev) => !prev)}
                type="button"
                aria-label="Share"
              >
                <Share2 className="w-6 h-6 text-white" />
              </button>

              {showShareMenu && (
                <>
                  {/* Mobile overlay */}
                  <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 md:hidden" />

                  {/* Share menu */}
                  <div className="fixed bottom-0 left-0 right-0 bg-neutral-900 rounded-t-3xl shadow-2xl z-50 md:absolute md:z-10 md:right-0 md:bottom-auto md:top-full md:left-auto md:mt-2 md:w-72 md:rounded-xl md:shadow-xl md:border md:border-neutral-800 animate-in slide-in-from-bottom-4 md:slide-in-from-top-2 duration-200">
                    {/* Mobile header */}
                    <div className="flex items-center justify-between p-6 border-b border-neutral-800 md:hidden">
                      <h3 className="text-xl font-semibold text-white">
                        Share Product
                      </h3>
                      <button
                        onClick={() => setShowShareMenu(false)}
                        className="p-2 hover:bg-neutral-800 rounded-full transition-colors"
                      >
                        <X className="w-6 h-6 text-neutral-400" />
                      </button>
                    </div>

                    {/* Share options */}
                    <div className="p-4 md:p-3 space-y-2">
                      {/* WhatsApp */}
                      <button
                        onClick={shareToWhatsApp}
                        className="w-full flex items-center gap-4 p-4 md:p-3 hover:bg-neutral-800 rounded-xl transition-all duration-200 group"
                      >
                        <div
                          style={{ backgroundColor: "#25d366" }}
                          className="w-10 h-10 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-200"
                        >
                          <WhatsappIcon size={20} className="text-white" />
                        </div>
                        <div className="flex-1 text-left">
                          <div className="font-semibold text-white text-sm">
                            WhatsApp
                          </div>
                        </div>
                      </button>

                      {/* Facebook */}
                      <button
                        onClick={shareToFacebook}
                        className="w-full flex items-center gap-4 p-4 md:p-3 hover:bg-neutral-800 rounded-xl transition-all duration-200 group"
                      >
                        <div
                          style={{ backgroundColor: "#0965fe" }}
                          className="w-10 h-10 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-200"
                        >
                          <span className="text-white font-bold text-lg">
                            f
                          </span>
                        </div>
                        <div className="flex-1 text-left">
                          <div className="font-semibold text-white text-sm">
                            Facebook
                          </div>
                        </div>
                      </button>

                      {/* Twitter */}
                      <button
                        onClick={shareToTwitter}
                        className="w-full flex items-center gap-4 p-4 md:p-3 hover:bg-neutral-800 rounded-xl transition-all duration-200 group"
                      >
                        <div
                          style={{ backgroundColor: "#1DA1F2" }}
                          className="w-10 h-10 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-200"
                        >
                          <span className="text-white font-bold text-sm">
                            X
                          </span>
                        </div>
                        <div className="flex-1 text-left">
                          <div className="font-semibold text-white text-sm">
                            Twitter
                          </div>
                        </div>
                      </button>

                      {/* Pinterest */}
                      <button
                        onClick={shareToPinterest}
                        className="w-full flex items-center gap-4 p-4 md:p-3 hover:bg-neutral-800 rounded-xl transition-all duration-200 group"
                      >
                        <div
                          style={{ backgroundColor: "#E60023" }}
                          className="w-10 h-10 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-200"
                        >
                          <span className="text-white font-bold text-lg">
                            P
                          </span>
                        </div>
                        <div className="flex-1 text-left">
                          <div className="font-semibold text-white text-sm">
                            Pinterest
                          </div>
                        </div>
                      </button>

                      {/* Email */}
                      <button
                        onClick={shareViaEmail}
                        className="w-full flex items-center gap-4 p-4 md:p-3 hover:bg-neutral-800 rounded-xl transition-all duration-200 group"
                      >
                        <div className="w-10 h-10 bg-neutral-700 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                          <Mail className="w-5 h-5 text-white" />
                        </div>
                        <div className="flex-1 text-left">
                          <div className="font-semibold text-white text-sm">
                            Email
                          </div>
                        </div>
                      </button>

                      {/* Copy Link */}
                      <button
                        onClick={copyToClipboard}
                        className="w-full flex items-center gap-4 p-4 md:p-3 hover:bg-neutral-800 rounded-xl transition-all duration-200 group"
                      >
                        <div className="w-10 h-10 bg-neutral-700 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform duration-200">
                          {copied ? (
                            <Check className="w-5 h-5 text-green-400" />
                          ) : (
                            <Copy className="w-5 h-5 text-white" />
                          )}
                        </div>
                        <div className="flex-1 text-left">
                          <div className="font-semibold text-white text-sm">
                            {copied ? "Copied!" : "Copy Link"}
                          </div>
                        </div>
                      </button>
                    </div>

                    {/* Mobile cancel button */}
                    <div className="p-4 border-t border-neutral-800 md:hidden">
                      <button
                        onClick={() => setShowShareMenu(false)}
                        className="w-full py-3 text-center text-neutral-400 font-medium"
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>

        {/* WhatsApp Order Button */}
        <button
          className="w-full mt-4 bg-green-600 text-white py-3 px-6 rounded-xl hover:bg-green-700 transition-colors font-medium shadow-lg shadow-green-900/20"
          onClick={orderByWhatsapp}
        >
          Order on WhatsApp
        </button>
      </div>
    </div>
  );
};

export default ProductInfo;
